interface IBorderObj {
  borderLineWidth?: number;
  borderColor?: string;
  borderargs?: number[];
}

export interface ImageObject {
  args: number[]; // 定位信息
  src: string; // 图片地址
  radius?: number; //  圆形渲染所需半径
  border?: IBorderObj;
}

export interface LoadedImageObject {
  img: HTMLImageElement; // 完成 load 的 image 对象
  args: number[];
  radius?: number;
  border?: any;
}

export interface QRCodeFrameObject {
  args: number[];
  color: string;
  lineWidth: number;
}

export interface TextObject {
  text: string;
  textAlign: CanvasTextAlign;
  font: string;
  color: string;
  dx: number;
  dy: number;
  maxWidth?: number;
  lineWidth?: number;
  letterSpacing?: number;
  strokeText?: string;
  strokeStyle?: string | CanvasGradient | CanvasPattern;
}

// 扩展 canvas 渲染能力：圆形图片
// @ts-ignore
CanvasRenderingContext2D.prototype.drawCircleImage = function(
  img: HTMLImageElement,
  x: number,
  y: number,
  r: number,
  width: number,
  height: number,
  border: any
) {
  this.save();
  // 画边框
  const borderX = border.borderargs[0];
  const borderY = border.borderargs[1];
  const borderWidth = border.borderargs[2];
  const borderHeight = border.borderargs[3];

  const point = function(x, y) {
    return { x: x, y: y };
  };
  const ptAa = point(borderX + r, borderY);
  const ptBb = point(borderX + borderWidth, borderY);
  const ptCc = point(borderX + borderWidth, borderY + borderHeight);
  const ptDd = point(borderX, borderY + borderHeight);
  const ptEe = point(borderX, borderY);

  this.beginPath();
  this.moveTo(ptAa.x, ptAa.y);
  this.arcTo(ptBb.x, ptBb.y, ptCc.x, ptCc.y, r);
  this.arcTo(ptCc.x, ptCc.y, ptDd.x, ptDd.y, r);
  this.arcTo(ptDd.x, ptDd.y, ptEe.x, ptEe.y, r);
  this.arcTo(ptEe.x, ptEe.y, ptAa.x, ptAa.y, r);
  this.lineWidth = border.borderLineWidth; // 宽度？？？
  this.strokeStyle = border.borderColor;
  this.stroke();
  // // this.closePath();
  // this.fillStyle = "green";
  // this.fill();

  // 图片
  const ptA = point(x + r, y);
  const ptB = point(x + width, y);
  const ptC = point(x + width, y + height);
  const ptD = point(x, y + height);
  const ptE = point(x, y);
  this.beginPath();
  this.moveTo(ptA.x, ptA.y);
  this.arcTo(ptB.x, ptB.y, ptC.x, ptC.y, r);
  this.arcTo(ptC.x, ptC.y, ptD.x, ptD.y, r);
  this.arcTo(ptD.x, ptD.y, ptE.x, ptE.y, r);
  this.arcTo(ptE.x, ptE.y, ptA.x, ptA.y, r);
  this.clip();
  this.drawImage(img, x, y, width, height);
  this.restore();
};

// 换行字符串
// @ts-ignore
CanvasRenderingContext2D.prototype.wrapText = function(
  text: { split: (arg0: string) => void },
  x: any,
  y: string | number,
  maxWidth: number,
  lineHeight: number,
  x2?: number, // new line start x"
  fixedX: number | undefined = 0,
  endPosition: number | undefined = 500
) {
  if (typeof text !== "string" || typeof x !== "number" || typeof y !== "number") {
    return;
  }

  const ctx = this;
  const canvas = ctx.canvas;

  if (typeof maxWidth === "undefined") {
    maxWidth = (canvas && canvas.width) || 300;
  }
  if (typeof lineHeight === "undefined") {
    lineHeight =
      (canvas && parseInt(window.getComputedStyle(canvas).lineHeight)) ||
      parseInt(window.getComputedStyle(document.body).lineHeight);
  }

  // 字符分隔为数组
  // @ts-ignore
  const arrText = text.split("");
  let line = "";
  let num = 0;
  for (let n = 0; n < arrText.length; n++) {
    const testLine = line + arrText[n];
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    const maxW = num === 0 ? maxWidth : endPosition;
    if (testWidth > maxW && n > 0) {
      if (num === 0) {
        ctx.fillText(line, x, y);
      } else {
        ctx.fillText(line, fixedX, y);
      }
      line = arrText[n];
      num++;
      y += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x2 || x, y);
};

// 画弧度
export function drawBorderRadius(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const point = (x: number, y: number) => ({ x, y });
  // 圆角半径不允许大于
  const maxRadius = width > height ? width / 2 : height / 2;
  const r = radius > maxRadius ? maxRadius : radius;
  const ptA = point(x + r, y);
  const ptB = point(x + width, y);
  const ptC = point(x + width, y + height);
  const ptD = point(x, y + height);
  const ptE = point(x, y);

  ctx.beginPath();
  ctx.moveTo(ptA.x, ptA.y);
  ctx.arcTo(ptB.x, ptB.y, ptC.x, ptC.y, r);
  ctx.arcTo(ptC.x, ptC.y, ptD.x, ptD.y, r);
  ctx.arcTo(ptD.x, ptD.y, ptE.x, ptE.y, r);
  ctx.arcTo(ptE.x, ptE.y, ptA.x, ptA.y, r);
}

// 加载图片
function loadImageWithArgs(obj: ImageObject): Promise<LoadedImageObject> {
  return new Promise((resolve, reject) => {
    const { args, src, radius, border } = obj;
    const img = new Image();
    const base64DataSrcRe = /^data:/;
    if (!base64DataSrcRe.test(src)) {
      // 规避base64图设置crossOrigin = 'Anonymous'引起的兼容问题
      img.crossOrigin = "Anonymous";
    }
    img.src = src;
    img.onload = () => {
      resolve({
        img,
        args,
        radius,
        border
      });
    };
    img.onerror = (err) => {
      reject(err);
    };
  });
}

// 预加载图片资源
function preloadImagesWithArgs(srcArr): Promise<LoadedImageObject[]> {
  if (!Array.isArray(srcArr)) {
    return Promise.reject();
  }

  return Promise.all(srcArr.map(loadImageWithArgs));
}

export function drawCanvas(
  backgroundImgURL: string,
  imgObjs: ImageObject[],
  textObjs: TextObject[],
  drawCtx: (ctx: CanvasRenderingContext2D) => void,
  cb: (base64: string) => void
) {
  return preloadImagesWithArgs(imgObjs).then((imgs) => {
    // 创建 canvas 元素
    const canvas = document.createElement("canvas");
    document.body.appendChild(canvas);
    // 准备工作
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("can not create context");
    }

    // 底图载入
    const background = new Image();
    background.crossOrigin = "Anonymous";

    background.onload = function() {
      // 使用底图大小定义 canvas 大小
      canvas.width = background.width;
      canvas.height = background.height;
      ctx.fillStyle = "rgba(0,0,0,0)";

      // 底图渲染
      ctx.drawImage(background, 0, 0);
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const rate = 1;

      // 渲染其他图片
      imgs.forEach((imgObj: LoadedImageObject) => {
        const { img, args, radius, border } = imgObj;
        if (args[2] === undefined) {
          // 对非定宽图片计算其自适应宽度以及左右处理
          args[2] = (args[3] * img.width) / img.height; // 计算图片的自适应宽度
          args[0] = (canvas.width / rate - args[2]) / 2; // 对自适应宽度图片进行水平居中处理
        }
        if (args[3] === undefined) {
          // 对非定高图片计算其自适应高度以及左右处理
          args[3] = (args[2] * img.height) / img.width; // 计算图片的自适应高度
          args[1] = (canvas.height / rate - args[3]) / 2; // 对自适应高度图片进行垂直居中处理
        }
        const ratedArgs = args.map((n) => n * rate);
        if (radius && border) {
          // @ts-ignore
          ctx.drawCircleImage(img, ratedArgs[0], ratedArgs[1], rate * radius, ratedArgs[2], ratedArgs[3], border);
          return;
        }
        // @ts-ignore
        ctx.drawImage(img, ...ratedArgs);
        if (border.borderLineWidth) {
          ctx.lineWidth = border.borderLineWidth;
          ctx.strokeStyle = border.borderColor;
          // @ts-ignore
          ctx.strokeRect(...border.borderargs);
        }
      });

      // 渲染文字
      textObjs.forEach((item: TextObject) => {
        if (item.letterSpacing) {
          canvas.style.letterSpacing = `${item.letterSpacing}px`;
        }

        ctx.fillStyle = item.color;
        ctx.font = item.font;
        ctx.textAlign = item.textAlign;
        ctx.textBaseline = "top";
        if (item.strokeText) {
          ctx.lineWidth = item.lineWidth || 3;

          if (item.strokeStyle) {
            ctx.strokeStyle = item.strokeStyle;
          }
          ctx.strokeText(item.strokeText, item.dx * rate, item.dy * rate);
          ctx.fillText(item.strokeText, item.dx * rate, item.dy * rate);
        } else {
          ctx.fillText(item.text, item.dx * rate, item.dy * rate);
        }
      });

      // 自定义操作ctx
      if (drawCtx) {
        drawCtx(ctx);
      }

      const base64Data = canvas.toDataURL("image/png");
      document.body.removeChild(canvas);
      cb(base64Data);
    };
    background.onerror = function() {
      throw new Error("图片加载失败");
    };
    // 触发载入
    background.src = backgroundImgURL;
  });
}
